import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { generateClient, SelectionSet } from 'aws-amplify/api';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Schema } from '../../../../../amplify/data/resource';
import Contacts from './Contacts/Contacts';
import FileList from './Files/FileList';
import Info from './Info';
import WhatsAppSidebar from './WhatsApp/WhatsAppSidebar';
import { useUtils } from '../../../../utils/context/useUtilsContext';

const selectionSet = [
  'id',
  'viewed',
  'createdAt',
  'path',
  'date',
  'status',
  'error',
  'type',
  'processed',
  'xmlVersion',
  'executionDate',
  'workEnvironment',
  'fileNumber',
  'sequenceNumber',
  'senderCode',
  'senderIdentifierType',
  'senderId',
  'senderName',
  'senderContactFirstName',
  'senderContactLastName',
  'senderContactPhone',
  'senderContactEmail',
  'senderContactCellular',
  'recipientCode',
  'recipientIdentifierType',
  'recipientId',
  'recipientCreatorIdentifier',
  'userId',
  'user.*',
  'deposits.totalDepositAmount',
  'deposits.fundIdentifier',
  'deposits.employees.employeeId',
  'deposits.employees.salaryMonth',
] as const;

// @ts-ignore
type File = SelectionSet<Schema['File']['type'], typeof selectionSet>;

const apiClient = generateClient<Schema>();

const Client: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { uuid } = useParams<{ uuid: string }>();

  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [infoAnchorEl, setInfoAnchorEl] = useState<null | HTMLElement>(null);
  const infoOpen = Boolean(infoAnchorEl);

  const [contactsAnchorEl, setContactsAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const contactsOpen = Boolean(contactsAnchorEl);

  const [whatsAppOpen, setWhatsAppOpen] = useState(false);

  const { openUploadDialog, closeUploadDialog, isUploadFileDialogOpen } =
    useUtils();

  const handleWhatsAppOpen = () => {
    setWhatsAppOpen(true);
  };

  const handleWhatsAppClose = () => {
    setWhatsAppOpen(false);
  };

  const handleInfoClick = (event: React.MouseEvent<HTMLImageElement>) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  const handleContactsClick = (event: React.MouseEvent<HTMLImageElement>) => {
    setContactsAnchorEl(event.currentTarget);
  };

  const handleContactsClose = () => {
    setContactsAnchorEl(null);
  };

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        if (!uuid) return;

        const response = await apiClient.models.User.get({ id: uuid });
        setClient(response.data);
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [uuid]);

  const [files, setFiles] = useState<File[]>([]);

  const fetchFiles = async () => {
    if (!uuid) return;
    setLoading(true);
    try {
      const { data, errors } = await apiClient.models.File.list({
        filter: { userId: { eq: uuid } },
        selectionSet: selectionSet,
      });
      if (errors) {
        console.error('Error fetching files:', errors);
      }

      setFiles((data || []) as File[]);
      setLoading(false);
    } catch (error) {
      console.error('Error during fetchFiles:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();

    if (!uuid) return;

    const createSub = apiClient.models.File.onCreate({
      filter: { userId: { eq: uuid } },
    }).subscribe({
      next: () => fetchFiles(),
      error: (error: string) => console.warn('onCreate error', error),
    });

    const updateSub = apiClient.models.File.onUpdate({
      filter: { userId: { eq: uuid } },
    }).subscribe({
      next: () => fetchFiles(),
      error: (error: string) => console.warn('onUpdate error', error),
    });

    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
    };
  }, [uuid]);

  const handleOpenUploadFile = () => {
    setSearchParams({ clientId: uuid || '' });
    openUploadDialog();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <img
            alt="logo"
            src={'/assets/header/file.svg'}
            onClick={handleOpenUploadFile}
            style={{ cursor: 'pointer' }}
          />
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <img
            alt="logo"
            src={'/assets/client/contacts.svg'}
            style={{ cursor: 'pointer' }}
            onClick={handleContactsClick}
          />
          <img
            alt="logo"
            src={'/assets/client/info.svg'}
            style={{ cursor: 'pointer' }}
            onClick={handleInfoClick}
          />
          <img
            alt="logo"
            src={'/assets/client/whatsapp.svg'}
            style={{ cursor: 'pointer' }}
            onClick={handleWhatsAppOpen}
          />

          <Typography
            fontSize="30px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
            textAlign={'right'}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {client?.companyName}
          </Typography>
        </Stack>
      </Box>
      <Divider
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.dark.border,
        }}
      />

      <FileList files={files} />
      <Info
        open={infoOpen}
        client={client}
        anchorEl={infoAnchorEl}
        onClose={handleInfoClose}
      />
      <Contacts
        open={contactsOpen}
        anchorEl={contactsAnchorEl}
        onClose={handleContactsClose}
      />
      <WhatsAppSidebar open={whatsAppOpen} onClose={handleWhatsAppClose} />
    </Box>
  );
};

export default Client;
